import React from 'react';
import { useTranslation } from 'react-i18next';
import { Column, AutoSizer, Table } from 'react-virtualized';
import { Empty, Spin } from 'antd';
import { AccountConfigurationEBP } from '../../../../../types/account_configurations';
import { getTimezoneOffset } from '../../../../utils/Utils';
import { Brand } from '../../../../../types/brand';
import ConfigurationHeaderCell from './ConfigurationHeaderCell';
import ConfigurationUsedChannels from './ConfigurationUsedChannels';
import ConfigurationUsedCustomFields from './ConfigurationUsedCustomFields';
import ConfigurationLastUsedApi from './ConfigurationLastUsedApi';

const ROW_HEIGHT = 50;

interface AccountEBPsTableProps {
  filteredData: AccountConfigurationEBP[];
  isLoading: boolean;
  brands: Brand[];
  selectedEBPIds: number[];
  setSelectedEBPIds: (ids: number[]) => void;
  selectedBrandIds: number[];
  setSelectedBrandIds: (ids: number[]) => void;
  sortColumn: string | undefined;
  sortOrder: string | undefined;
  onSort: (column: string, order: string) => void;
  nameListValues: { id: number; name: string }[];
  brandListValues: { id: number; name: string }[];
}

const AccountEBPsTable: React.FC<AccountEBPsTableProps> = ({
  filteredData,
  isLoading,
  brands,
  selectedEBPIds,
  setSelectedEBPIds,
  selectedBrandIds,
  setSelectedBrandIds,
  sortColumn,
  sortOrder,
  onSort,
  nameListValues,
  brandListValues,
}) => {
  const { t } = useTranslation();
  const timezoneOffset = React.useMemo(() => getTimezoneOffset(), []);

  const renderEBPBrand = (brandId: number) => {
    if (!brandId) {
      return <span />;
    }

    const brand = brands.find(b => b.id === brandId);
    return brand ? <span>{brand.name}</span> : <span />;
  };

  const renderUsedDynamicConfigurations = (used: boolean) => {
    if (used) {
      return <span>{t('common:yes')}</span>;
    }
    return <span>{t('common:no')}</span>;
  };

  const createNameHeaderCell = () => (
    <ConfigurationHeaderCell
      label={t('configurations:name')}
      filterValues={nameListValues}
      handleFilterChange={setSelectedEBPIds}
      selectedFilterIds={selectedEBPIds}
      showSearch
      sortColumn="name"
      currentSortColumn={sortColumn}
      sortOrder={sortOrder}
      onSort={order => onSort('name', order)}
    />
  );

  const createBrandHeaderCell = () => (
    <ConfigurationHeaderCell
      label={t('configurations:brand')}
      filterValues={brandListValues}
      handleFilterChange={setSelectedBrandIds}
      selectedFilterIds={selectedBrandIds}
      showSearch
      sortColumn="parent_owner_brand_id"
      currentSortColumn={sortColumn}
      sortOrder={sortOrder}
      onSort={order => onSort('parent_owner_brand_id', order)}
    />
  );

  return (
    <div className="h-full w-full overflow-hidden bg-white">
      <AutoSizer>
        {({ height, width }) => (
          <div style={{ height, width }}>
            <Table
              key={`table-${filteredData.length}`}
              height={height}
              width={width - 2}
              headerHeight={ROW_HEIGHT}
              rowHeight={ROW_HEIGHT}
              rowCount={filteredData.length}
              rowGetter={({ index }) => filteredData[index]}
              noRowsRenderer={() =>
                isLoading ? (
                  <Spin className="spinner-center" style={{ marginTop: '20px' }} />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('configurations:noEBPs')}
                  />
                )
              }
            >
              <Column label={t('configurations:id')} dataKey="id" width={100} />
              <Column
                headerRenderer={createNameHeaderCell}
                dataKey="name"
                width={120}
                flexGrow={1}
              />
              <Column
                headerRenderer={createBrandHeaderCell}
                dataKey="parent_owner_brand_id"
                width={200}
                cellRenderer={({ cellData }) => renderEBPBrand(cellData)}
              />
              <Column
                label={t('configurations:usedChannels')}
                dataKey="used_channels"
                width={200}
                cellRenderer={({ cellData }) => <ConfigurationUsedChannels channels={cellData} />}
              />
              <Column
                label={t('configurations:usedCustomFields')}
                dataKey="used_custom_fields"
                width={200}
                cellRenderer={({ cellData }) => (
                  <ConfigurationUsedCustomFields customFields={cellData} />
                )}
              />
              <Column
                label={t('configurations:usedInDynamicConfigurations')}
                dataKey="used_dynamic_configurations"
                width={200}
                cellRenderer={({ cellData }) => renderUsedDynamicConfigurations(cellData)}
              />
              <Column
                label={`${t('configurations:lastUsedApi')} (${timezoneOffset})`}
                dataKey="last_used_api"
                width={200}
                cellRenderer={({ cellData }) => <ConfigurationLastUsedApi lastUsedApi={cellData} />}
              />
            </Table>
          </div>
        )}
      </AutoSizer>
    </div>
  );
};

export default AccountEBPsTable;
